import { Button } from "@tracify/ui/button";
import { Card, CardContent } from "@tracify/ui/card";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import React, { useMemo, useState } from "react";
import type {
  AdAccountIssues,
  ConnectedAdAccounts,
} from "@lib/api-hooks/useAdAccounts/types";
import { useConnectedAdAccounts } from "@lib/api-hooks/useAdAccounts/useConnectedAdAccounts";
import { Banner } from "@tracify/ui/banner";
import { Separator } from "@tracify/ui/separator";
import InitializeAdAccountFlowModal from "@components/Dashboards/SettingsDashboard/InitializeAdAccountFlowModal";
import { integrations } from "@components/Dashboards/SettingsDashboard/CombinedIntegrationsDashboard";
import { useAdAccountIssues } from "@lib/api-hooks/useAdAccounts/useAdAccountIssues";

type Props = {
  type:
    | "google"
    | "facebook"
    | "tiktok"
    | "pinterest"
    | "outbrain"
    | "msads"
    | "taboola";
};

const ReconnectAdAccountsCard = ({ type }: Props) => {
  const { selectedOrganisationData } = useSelectedStores();
  const { data: connectedAdAccounts } = useConnectedAdAccounts({
    organisationId: selectedOrganisationData?.id ?? "",
    includeAll: false,
  });
  const { data: adAccountIssues } = useAdAccountIssues({
    enabled: !!selectedOrganisationData?.id,
  });

  const [open, setOpen] = useState(false);
  const [accountIdToReconnect, setAccountIdToReconnect] = useState<
    string | null
  >(null);
  const adAccountsForChannel = useMemo(() => {
    return (
      connectedAdAccounts?.accounts?.[
        type as keyof ConnectedAdAccounts["accounts"]
      ] ?? []
    );
  }, [connectedAdAccounts, type]);

  const issuesForChannel = useMemo(() => {
    const issueRecords = adAccountIssues?.[type as keyof AdAccountIssues];
    if (!issueRecords) {
      return [];
    }
    return Object.entries(issueRecords)
      .filter(
        ([accountId, issues]) =>
          issues.length > 0 &&
          adAccountsForChannel.some(
            (el) =>
              el.account_id === accountId && el.connected_stores?.length > 0
          )
      )
      .map(([accountId, issues]) => ({
        accountId,
        issues,
      }));
  }, [adAccountIssues, adAccountsForChannel, type]);

  const noIssues = !issuesForChannel?.length;
  if (noIssues) {
    return null;
  }
  return (
    <>
      <InitializeAdAccountFlowModal
        open={open}
        onClose={() => {
          setOpen(false);
          setAccountIdToReconnect(null);
        }}
        integration={integrations[type]}
        type={type === "facebook" ? "fb" : type}
        isReconnect={true}
        accountId={accountIdToReconnect ?? undefined}
      />
      <Card className="overflow-hidden">
        <Banner
          messages={[
            {
              id: "reconnect-ad-accounts",
              type: "error",
              message:
                "We are experiencing connection issues with some of your ad accounts.",
              dismissible: false,
            },
          ]}
          className="rounded-b-none text-sm"
        />
        {issuesForChannel?.map((el, index) => {
          const account = adAccountsForChannel.find(
            (account) => el.accountId === account.account_id
          );
          if (!account) {
            return null;
          }
          return (
            <React.Fragment key={account.account_id}>
              {index > 0 && <Separator />}
              <CardContent className="text-sm mt-4">
                <p className="wrap-break-word">
                  Your account{" "}
                  <span className="font-medium">{account.name}</span>
                  <span className="hidden md:inline">
                    {" "}
                    ({account.account_id})
                  </span>{" "}
                  has the following issue(s):
                </p>
                <p className="text-xs text-muted-foreground mt-1 break-all md:hidden">
                  ID: {account.account_id}
                </p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  {el.issues.map((issue) => (
                    <li key={issue} className="wrap-break-word">
                      {issue}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-col gap-3 mt-4 md:flex-row md:items-center md:gap-4 md:mt-6">
                  <div className="order-2 md:order-0">
                    <p className="font-medium text-sm wrap-break-word">
                      {account.name}
                    </p>
                    <p className="text-xs italic mt-1 break-all">
                      {account.account_id}
                    </p>
                  </div>
                  <Button
                    onClick={() => {
                      setAccountIdToReconnect(account.account_id);
                      setOpen(true);
                    }}
                    className="w-full order-1 md:w-auto md:order-0"
                  >
                    Reconnect
                  </Button>
                </div>
              </CardContent>
            </React.Fragment>
          );
        })}
      </Card>
    </>
  );
};

export default ReconnectAdAccountsCard;
